import { getStorage, uploadBytes, getDownloadURL, ref } from "firebase/storage"
import { getAuth } from "firebase/auth"
import { PDFDocument } from "pdf-lib"
import JSZip from "jszip"
import getFirebase from "../../firebase"
import { getDevToken, getPost } from "../utils/commHelper"

const isDev = process.env.NODE_ENV === "development"
const SERVER_BASE_URL = isDev
  ? "https://api.dev.tulu.io"
  : "https://api.tulu.io"

async function resolveAuthToken() {
  const devToken = getDevToken()
  if (devToken) return devToken
  const auth = getAuth(getFirebase())
  // Wait for Firebase Auth's initial session restore — <Login> can render children
  // off a cached localStorage user before that finishes, when currentUser is still null.
  await auth.authStateReady()
  const user = auth.currentUser
  if (!user) throw new Error("Not authenticated")
  return user.getIdToken()
}

async function requestPresignedPut({ bucket, folder, filename, contentType }) {
  const token = await resolveAuthToken()
  const res = await fetch(`${SERVER_BASE_URL}/uploads/presignPut`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      token,
      source: "tulu_website",
    },
    body: JSON.stringify({ bucket, folder, filename, contentType }),
  })
  if (!res.ok) {
    const errBody = await res.text()
    throw new Error(`presignPut failed (${res.status}): ${errBody}`)
  }
  return res.json()
}

function putToS3({ uploadUrl, file, contentType, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", uploadUrl, true)
    xhr.setRequestHeader("Content-Type", contentType)
    xhr.setRequestHeader("x-amz-acl", "public-read")
    xhr.upload.onprogress = evt => {
      if (evt.lengthComputable && onProgress) {
        onProgress(Math.round((evt.loaded / evt.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`))
      }
    }
    xhr.onerror = () => reject(new Error("S3 PUT network error"))
    xhr.send(file)
  })
}

const uploadFile = async (file, selectedBucket, onSuccess, onProgress) => {
  try {
    const contentType = file.type || "application/octet-stream"
    const { uploadUrl, publicUrl } = await requestPresignedPut({
      bucket: "tulu-assets",
      folder: selectedBucket,
      filename: file.name,
      contentType,
    })
    await putToS3({ uploadUrl, file, contentType, onProgress })
    onSuccess(publicUrl)
  } catch (err) {
    console.error("uploadFile error", err)
  }
}

// Same upload path as `uploadFile` above, but promise-based (throws on failure
// instead of only logging) — needed by convertAndCountPages, which must know
// whether the upload actually succeeded before calling the paid conversion API.
async function uploadFileAsync(file, selectedBucket) {
  const contentType = file.type || "application/octet-stream"
  const { uploadUrl, publicUrl } = await requestPresignedPut({
    bucket: "tulu-assets",
    folder: selectedBucket,
    filename: file.name,
    contentType,
  })
  await putToS3({ uploadUrl, file, contentType })
  return publicUrl
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i
const OFFICE_MIME_DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
const OFFICE_MIME_PPTX = "application/vnd.openxmlformats-officedocument.presentationml.presentation"

// Matches tulu_app's own allow-list (printer_add_file.dart) plus common image
// types — anything outside this fails fast with a clear message instead of
// silently hitting the paid conversion endpoint.
const SUPPORTED_EXTENSIONS = /\.(pdf|docx?|pptx?|xlsx?|txt|png|jpe?g|gif|webp|bmp|heic|heif)$/i

export const isSupportedFileType = (file) => {
  if (!file) return false
  if ((file.type || "").startsWith("image/")) return true
  return SUPPORTED_EXTENSIONS.test(file.name || "")
}

function getExtension(file) {
  const match = /\.([a-z0-9]+)$/i.exec(file.name || "")
  return match ? match[1].toLowerCase() : ""
}

async function countPdfPagesFromBuffer(buffer) {
  try {
    const pdf = await PDFDocument.load(buffer, { ignoreEncryption: true })
    const count = pdf.getPageCount()
    return count > 0 ? count : null
  } catch (err) {
    console.warn("[Printer] could not read PDF page count:", err?.message || err)
    return null
  }
}

// DOCX carries a page count Word itself cached at last-save time
// (docProps/app.xml's <Pages>) — free and instant to read, no network call.
// Can rarely be stale if the file was edited/exported by something other than
// Word; returns null (not a guess) whenever that tag is missing so the caller
// falls back to the paid conversion path instead of trusting a guess.
async function getDocxPageCount(file) {
  try {
    const buffer = await file.arrayBuffer()
    const zip = await JSZip.loadAsync(buffer)
    const appXmlFile = zip.file("docProps/app.xml")
    if (!appXmlFile) return null
    const appXml = await appXmlFile.async("text")
    const match = /<Pages>(\d+)<\/Pages>/i.exec(appXml)
    if (!match) return null
    const count = parseInt(match[1], 10)
    return count > 0 ? count : null
  } catch (err) {
    console.warn("[Printer] could not read DOCX page count:", err?.message || err)
    return null
  }
}

// PPTX's slide count is exact (not a cached estimate like DOCX's page count) —
// counted directly from the archive's slide entries, one slide == one printed page.
async function getPptxSlideCount(file) {
  try {
    const buffer = await file.arrayBuffer()
    const zip = await JSZip.loadAsync(buffer)
    const slideCount = Object.keys(zip.files).filter((name) =>
      /^ppt\/slides\/slide\d+\.xml$/i.test(name)
    ).length
    return slideCount > 0 ? slideCount : null
  } catch (err) {
    console.warn("[Printer] could not read PPTX slide count:", err?.message || err)
    return null
  }
}

/**
 * Reads the real page count from a File/Blob so the dashboard bills the actual
 * number of pages instead of a guess — free, local, no network call. Handles:
 * images (always 1 page), PDFs (pdf-lib), DOCX (Word's own cached page count),
 * and PPTX (exact slide count). Returns `null` (not a fallback number) for
 * anything else — including a DOCX missing its cached page count, or XLSX,
 * which has no equivalent reliable field — so callers can tell "known count"
 * apart from "couldn't detect" and fall back to `convertAndCountPages` (paid)
 * rather than silently under-billing.
 */
export const getPdfPageCount = async (file) => {
  if (!file) return null

  const looksLikeImage =
    (file.type || "").startsWith("image/") || IMAGE_EXTENSIONS.test(file.name || "")
  if (looksLikeImage) return 1

  const looksLikePdf =
    file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")
  if (looksLikePdf) {
    const buffer = await file.arrayBuffer()
    return countPdfPagesFromBuffer(buffer)
  }

  const ext = getExtension(file)
  if (ext === "docx" || file.type === OFFICE_MIME_DOCX) {
    return getDocxPageCount(file)
  }
  if (ext === "pptx" || file.type === OFFICE_MIME_PPTX) {
    return getPptxSlideCount(file)
  }

  return null
}

/**
 * Paid fallback for file types getPdfPageCount can't count for free (XLSX,
 * TXT, legacy binary .doc/.xls/.ppt, or a DOCX missing its cached page count):
 * uploads the original file, converts it via tulu_server's existing
 * `POST /convertToPdf` (api2pdf.com/LibreOffice via a metered RapidAPI plan —
 * the same endpoint tulu_app's own print flow already uses), then counts pages
 * on the resulting PDF. Returns `{ pages, pdfUrl, pdfFileName }` — the RPI must
 * print the converted PDF, not the original file — or `null` on any failure
 * (never throws), matching this file's existing error-swallowing style.
 */
export const convertAndCountPages = async (file, isDev = false) => {
  if (!file) return null
  try {
    const originalUrl = await uploadFileAsync(file, "operation_upload_printer_files")
    const res = await getPost("/convertToPdf", { url: originalUrl, fileName: file.name }, isDev)
    const pdfUrl = res?.data?.result?.pdfUrl ?? res?.data?.pdfUrl
    if (!pdfUrl) {
      console.warn("[Printer] convertToPdf did not return a pdfUrl")
      return null
    }
    const pdfRes = await fetch(pdfUrl)
    if (!pdfRes.ok) return null
    const buffer = await pdfRes.arrayBuffer()
    const pages = await countPdfPagesFromBuffer(buffer)
    if (!pages) return null
    const pdfFileName = (file.name || "file").replace(/\.[^.]+$/, "") + ".pdf"
    return { pages, pdfUrl, pdfFileName }
  } catch (err) {
    console.error("[Printer] convertAndCountPages failed:", err?.message || err)
    return null
  }
}

export const singleFileUploadHandler = (
  selectedFile,
  selectedBucket,
  onSuccess,
  onProgress
) => {
  uploadFile(selectedFile, selectedBucket, onSuccess, onProgress)
}

export const uploadFiles = async (file, title, type) => {
  const storage = getStorage()
  const storageRef = ref(storage, "images/" + title + "_" + type + ".pdf")
  const metadata = {
    contentType: file.type,
  }

  const snapshot = await uploadBytes(storageRef, file, metadata)

  const url = await getDownloadURL(snapshot.ref)
  console.log(url)
  return url
}
